import { buildHref, matchRoute } from "@softmax/web-addresses";

import { type GamePageContext } from "../../game-types";
import { onePagerCoworldOf } from "@/features/play/state";
import { viewerState } from "./viewer";
import { type AdmissionDecision } from "./admission.types";
import { playSignedInRoutes, signedInRoutes } from "./route-access";

export async function unflaggedAdmission({
  signal,
  url,
  game,
}: {
  signal?: AbortSignal | undefined;
  url: URL;
  game: GamePageContext | null;
}): Promise<AdmissionDecision> {
  const route = matchRoute(url.pathname);

  if (route.name !== "game.start" && !playSignedInRoutes.has(route.name))
    return { kind: "not-found" };

  const viewer = await viewerState({
    signal,
    game,
  });

  if (viewer === "unavailable")
    return { kind: "unavailable", message: "Session is unavailable" };

  if (viewer === "signed-in") return { kind: "not-found" };

  if (route.name !== "game.start")
    return { kind: "navigate", navigation: { kind: "sign-in" } };

  const address = game?.address ?? {
    slug: route.gameSlug,
    catalog_status: route.catalogScope,
  };
  return {
    kind: "navigate",
    navigation: {
      kind: "path",
      pathname: route.league
        ? buildHref({
            name: "game.league",
            game: address,
            league: route.league,
          })
        : buildHref({ name: "game.home", game: address }),
      status: 307,
    },
  };
}

export async function signedInAdmission({
  signal,
  url,
  game,
}: {
  signal?: AbortSignal | undefined;
  url: URL;
  game: GamePageContext | null;
}): Promise<AdmissionDecision> {
  const route = matchRoute(url.pathname);

  if (!signedInRoutes.has(route.name)) return { kind: "allowed" };

  if (route.name === "game.play" && game && onePagerCoworldOf(game.corpusSlug))
    return { kind: "allowed" };

  const viewer = await viewerState({
    signal,
    game,
  });

  if (viewer === "unavailable")
    return { kind: "unavailable", message: "Session is unavailable" };

  if (viewer === "signed-in") return { kind: "allowed" };

  return {
    kind: "navigate",
    navigation: { kind: "sign-in" },
  };
}
